/**
 * Chunk Error Handler
 * 
 * Handles stale chunk errors that occur when:
 * 1. A new deployment happens with different chunk hashes
 * 2. Users have old HTML cached that references old chunks
 * 3. Dynamic imports fail with 404
 * 
 * Solution: Detect chunk load failures and force a page reload.
 * Uses sessionStorage to prevent infinite reload loops.
 * 
 * @see https://vitejs.dev/guide/build.html#chunking-strategy
 */

const RELOAD_KEY = 'chunk_error_reload';
const RELOAD_TIMESTAMP_KEY = 'chunk_error_reload_time';
const MAX_RELOADS = 2;
const RELOAD_WINDOW_MS = 30000; // 30 seconds

/**
 * Check if an error is a chunk load failure
 */
function isChunkLoadError(error: Error): boolean {
  const message = error.message.toLowerCase();
  
  // Vite dynamic import failures
  if (message.includes('failed to fetch dynamically imported module')) {
    return true;
  }
  
  // Generic chunk load failures
  if (message.includes('loading chunk') && message.includes('failed')) {
    return true;
  }
  
  // CSS chunk failures
  if (message.includes('loading css chunk') && message.includes('failed')) {
    return true;
  }
  
  // Network errors on .js files (likely stale chunks)
  if (error.name === 'TypeError' && message.includes('failed to fetch')) {
    return true;
  }
  
  // MIME type mismatch — Vercel returns index.html (text/html) for missing chunks
  // Browser rejects it because it expects application/javascript
  if (message.includes('is not a valid javascript mime type')) {
    return true;
  }
  
  // Syntax error from trying to parse HTML as JS (alternate manifestation)
  if (error.name === 'SyntaxError' && message.includes('unexpected token \'<\'')) {
    return true;
  }
  
  return false;
}

/**
 * Check if we should reload (prevents infinite loops)
 */
function shouldReload(): boolean {
  try {
    const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
    const lastReloadTime = parseInt(sessionStorage.getItem(RELOAD_TIMESTAMP_KEY) || '0', 10);
    const now = Date.now();
    
    // Reset counter if outside the window
    if (now - lastReloadTime > RELOAD_WINDOW_MS) {
      sessionStorage.setItem(RELOAD_KEY, '0');
      return true;
    }
    
    // Don't reload if we've hit the limit
    if (reloadCount >= MAX_RELOADS) {
      console.error('[ChunkErrorHandler] Max reloads reached, not reloading again');
      return false;
    }
    
    return true;
  } catch {
    // sessionStorage not available, allow reload
    return true;
  }
}

/**
 * Build a cache-busting URL that properly handles query strings and hash fragments.
 * 
 * URL structure: protocol://host/path?query#hash
 * Cache buster must go in query string, BEFORE the hash.
 * 
 * Examples:
 * - /page → /page?_=123
 * - /page?foo=bar → /page?foo=bar&_=123
 * - /page#section → /page?_=123#section
 * - /page?foo=bar#section → /page?foo=bar&_=123#section
 */
function buildCacheBustingUrl(): string {
  const url = new URL(window.location.href);
  url.searchParams.set('_', String(Date.now()));
  return url.toString();
}

/**
 * Increment reload counter, clear caches, and reload the page
 */
function reloadPage(): void {
  try {
    const reloadCount = parseInt(sessionStorage.getItem(RELOAD_KEY) || '0', 10);
    sessionStorage.setItem(RELOAD_KEY, String(reloadCount + 1));
    sessionStorage.setItem(RELOAD_TIMESTAMP_KEY, String(Date.now()));
  } catch {
    // sessionStorage not available, proceed anyway
  }
  
  const cacheBustedUrl = buildCacheBustingUrl();
  
  // Clear all CacheStorage entries (stale SW caches, etc.)
  const cacheCleanup = 'caches' in window
    ? caches.keys().then(names => Promise.all(names.map(n => caches.delete(n))))
    : Promise.resolve();
  
  cacheCleanup.finally(() => {
    // location.replace avoids polluting browser history with reload entries
    window.location.replace(cacheBustedUrl);
  });
}

/**
 * Handle a chunk load error
 * Returns true if the error was handled (page will reload)
 */
export function handleChunkError(error: Error): boolean {
  if (!isChunkLoadError(error)) {
    return false;
  }
  
  console.warn('[ChunkErrorHandler] Detected stale chunk, checking if reload is safe...');
  
  if (shouldReload()) {
    console.warn('[ChunkErrorHandler] Reloading page to fetch fresh chunks...');
    reloadPage();
    return true;
  }
  
  return false;
}

/**
 * Clear the reload counter (call on successful page load)
 */
export function clearChunkErrorState(): void {
  try {
    sessionStorage.removeItem(RELOAD_KEY);
    sessionStorage.removeItem(RELOAD_TIMESTAMP_KEY);
  } catch {
    // Ignore
  }
}

/**
 * Setup global handler for chunk errors
 * Call this once during app initialization
 */
export function setupChunkErrorHandler(): void {
  // Handle unhandled promise rejections (dynamic imports)
  window.addEventListener('unhandledrejection', (event) => {
    const error = event.reason instanceof Error 
      ? event.reason 
      : new Error(String(event.reason));
    
    if (handleChunkError(error)) {
      event.preventDefault(); // Prevent default error logging
    }
  });
  
  // Handle script load errors
  window.addEventListener('error', (event) => {
    // Check if it's a script error (not a runtime error)
    if (event.target && (event.target as HTMLElement).tagName === 'SCRIPT') {
      const scriptSrc = (event.target as HTMLScriptElement).src || '';
      
      // Check if it's a Vite chunk
      if (scriptSrc.includes('/assets/') && scriptSrc.endsWith('.js')) {
        console.warn('[ChunkErrorHandler] Script load failed:', scriptSrc);
        
        if (shouldReload()) {
          console.warn('[ChunkErrorHandler] Reloading page to fetch fresh chunks...');
          reloadPage();
        }
      }
    }
  }, true); // Use capture phase to catch before other handlers
  
  // Clear state on successful load (after a short delay to ensure page is stable)
  setTimeout(() => {
    clearChunkErrorState();
  }, 5000);
}

export default setupChunkErrorHandler;
